import { TimeBucket } from '@/Enums/ReportBuilder/TimeBucket.enum';
import { Grid } from '@/components/Grid';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { getMockRows } from '@/helpers/reportBuilder';
import { PreviewGridColumnSource } from '@/types/ReportBuilder/PreviewGridColumn.types';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import GridToolBar from '../Components/GridToolbar';
import NoRowsOverlay from './NoRowsOverlay';
import { getAllColumnsHiddenModel, getGridColumns } from './columns';

interface IProps {
  timeBucket?: TimeBucket | '';
  visibleColumns?: PreviewGridColumnSource[];
  columns: PreviewGridColumnSource[];
}

const ReportPreviewGrid = ({ columns, visibleColumns, timeBucket }: IProps) => {
  const apiRef = useGridApiRef();

  const allHiddenModel = useMemo(() => getAllColumnsHiddenModel(columns), [columns]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(allHiddenModel);

  useEffect(() => {
    const visibilityModel = !visibleColumns?.length
      ? allHiddenModel
      : {
          ...allHiddenModel,
          ...Object.fromEntries(
            visibleColumns?.map(column => {
              return [column.value, true];
            }),
          ),
          timeBucket: !!timeBucket && !!visibleColumns?.length,
        };
    setColumnVisibilityModel(visibilityModel);
  }, [timeBucket, visibleColumns, allHiddenModel]);

  useEffect(() => {
    if (Object.values(columnVisibilityModel).filter(Boolean).length > 7) {
      return;
    }
    apiRef?.current?.autosizeColumns?.(EXPAND_OPTIONS);
  }, [columnVisibilityModel, apiRef]);

  const rows = useMemo(
    () => (visibleColumns?.length ? getMockRows(timeBucket) : []),
    [visibleColumns?.length, timeBucket],
  );

  const gridColumns = useMemo(() => getGridColumns(columns), [columns]);

  return (
    <Grid
      sx={{
        border: 'none',
        minHeight: 0,
        [`& .${gridClasses.main}`]: {
          borderBottom: '1px solid rgb(227, 227, 227)',
        },
        [`& .${gridClasses.columnHeaders}`]: {
          borderRadius: 0,
          borderTop: '1px solid rgb(227, 227, 227)',
        },
      }}
      apiRef={apiRef}
      autosizeOnMount
      autosizeOptions={EXPAND_OPTIONS}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      showCellVerticalBorder
      showColumnVerticalBorder
      rows={rows}
      columns={gridColumns}
      columnHeaderHeight={30}
      slots={{
        toolbar: GridToolBar,
        columnSortedAscendingIcon: KeyboardArrowUp,
        columnSortedDescendingIcon: KeyboardArrowDown,
        noRowsOverlay: NoRowsOverlay,
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      slotProps={{
        toolbar: {
          title: 'Report Data Preview',
        },
      }}
    />
  );
};

export default ReportPreviewGrid;
